import { isValidNmfcLength } from '@fv/models'

import { useNmfcArticleSearch } from './queries'
import { formatSubNumber, isUnshippable, parseNmfc } from './utils'

type ValidatorArgs = {
  code: string
  freightClass?: number
}
type ValidationState = 'valid' | 'error' | 'unknown' | 'loading'
export type NmfcValidationResult = {
  state: ValidationState
  hint?: string
}
export const useNmfcValidator = ({
  code,
  freightClass,
}: ValidatorArgs): NmfcValidationResult => {
  const { nmfc, sub } = parseNmfc(code)
  const articleSearch = useNmfcArticleSearch({ code: nmfc })

  if (!code) {
    return {
      state: 'unknown',
    }
  }

  if (!isValidNmfcLength(nmfc)) {
    return {
      state: 'error',
      hint: 'This is not a valid NMFC',
    }
  }

  if (articleSearch.isFetching) {
    return {
      state: 'loading',
    }
  }

  if (!articleSearch.data) {
    return {
      state: 'error',
      hint: 'This NMFC was not found',
    }
  }

  if (articleSearch.data.isCanceled) {
    return {
      state: 'error',
      hint: 'This NMFC has been canceled',
    }
  }

  if (isUnshippable(articleSearch.data)) {
    return {
      state: 'error',
      hint: 'This NMFC is not shippable',
    }
  }

  if (!sub && !articleSearch.data.subItems.length) {
    const classMatch =
      !freightClass || freightClass === articleSearch.data.freightClass
    return {
      state: classMatch ? 'valid' : 'error',
      ...(!classMatch && {
        hint: `The recommended freight class for this article is ${articleSearch.data.freightClass}`,
      }),
    }
  }

  if (!sub && articleSearch.data.subItems.length) {
    return {
      state: 'error',
      hint: 'This NMFC requires a sub number',
    }
  }

  if (sub) {
    const foundSub = articleSearch.data.subItems.find(
      subItem => formatSubNumber(subItem.subNumber) === sub,
    )
    if (!foundSub) {
      return {
        state: 'error',
        hint: 'No matching sub was found for this NMFC',
      }
    }

    const classMatch = !freightClass || freightClass === foundSub.freightClass
    return {
      state: classMatch ? 'valid' : 'error',
      ...(!classMatch && {
        hint: `The recommended freight class for this sub is ${foundSub.freightClass}`,
      }),
    }
  }

  return {
    state: 'unknown',
  }
}
